import { css } from '@emotion/react';

import { classes as buttonBaseClasses } from '@core/ButtonBase/getButtonBaseCss';
import { typography } from '@core/Typography2';
import generateEmotionClasses from '@core/utils/generateEmotionClasses';

export const classes = generateEmotionClasses('Chip', [
  'root',
  'selected',
  'deletable',
  'deleteIcon',
  'startIcon',
  'label',
  'labelText',
]);

const getChipCss = css`
  &.${classes.root} {
    ${typography.bodySecondary};
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    min-width: 44px;
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
    color: var(--cds-color-neutral-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-400);
    background-color: var(--cds-color-neutral-background-primary);

    &:hover {
      color: var(--cds-color-interactive-primary-hover);
      border-color: var(--cds-color-interactive-primary-hover);
      background-color: var(
        --cds-color-interactive-background-primary-hover-weak
      );
    }

    &:active {
      color: var(--cds-color-interactive-primary-pressed);
      border-color: var(--cds-color-interactive-primary-pressed);
      background-color: var(
        --cds-color-interactive-background-primary-pressed-weak
      );
    }

    &.${buttonBaseClasses.focusVisible}::after {
      border-radius: var(--cds-border-radius-400);
    }
  }

  &.${classes.selected} {
    ${typography.actionSecondary};
    color: var(--cds-color-neutral-primary-invert);
    border-color: var(--cds-color-interactive-secondary-selected);
    background-color: var(--cds-color-interactive-secondary-selected);

    &:hover {
      color: var(--cds-color-neutral-primary-invert);
      border-color: var(--cds-color-interactive-primary-hover);
      background-color: var(--cds-color-interactive-primary-hover);
    }

    &:active {
      color: var(--cds-color-neutral-primary-invert);
      border-color: var(--cds-color-interactive-primary-pressed);
      background-color: var(--cds-color-interactive-primary-pressed);
    }
  }

  &.${classes.deletable} {
    padding: 0 var(--cds-spacing-100) 0 var(--cds-spacing-150);
    color: var(--cds-color-neutral-primary);

    .${classes.deleteIcon} {
      color: var(--cds-color-neutral-primary-weak);
    }

    &:hover .${classes.deleteIcon} {
      color: var(--cds-color-interactive-primary-hover);
    }

    &:active .${classes.deleteIcon} {
      color: var(--cds-color-interactive-primary-pressed);
    }
  }

  .${classes.startIcon} {
    margin-left: 0;
    display: inherit;
    margin-right: var(--cds-spacing-50);
  }

  .${classes.label} {
    width: 100%;
    display: inherit;
    align-items: inherit;
    justify-content: inherit;
  }

  .${classes.labelText} {
    /*
     * display: grid lets the hidden ::after spacer share column 1 with the
     * real text. The column is sized to max(normal-weight, bold-weight) so the
     * chip never shifts width when the selected state changes font-weight.
     */
    display: grid;

    &[data-label]::after {
      content: attr(data-label);

      /* Mirror actionSecondary metrics (font-weight-600 + letter-spacing-100) */
      font-weight: var(--cds-font-weight-600);
      letter-spacing: var(--cds-letter-spacing-100);

      /* Inherit font-family so metrics match the visible text exactly */
      font-family: inherit;

      /*
       * visibility: hidden removes this spacer from the accessibility tree;
       * height: 0 removes it from the vertical layout.
       */
      height: 0;
      overflow: hidden;
      visibility: hidden;
      pointer-events: none;
      user-select: none;
    }
  }
`;

export default getChipCss;
